/////////////////////////////////////////////////////////////////////////////////////////////
// Copyright 2026 Garmin International, Inc.
// Licensed under the Flexible and Interoperable Data Transfer (FIT) Protocol License; you
// may not use this file except in compliance with the Flexible and Interoperable Data
// Transfer (FIT) Protocol License.
/////////////////////////////////////////////////////////////////////////////////////////////
// ****WARNING****  This file is auto-generated!  Do NOT edit this file.
// Profile Version = 21.218.0Release
// Tag = production/release/21.218.0-0-g96fee6d7
/////////////////////////////////////////////////////////////////////////////////////////////

@file:OptIn(ExperimentalJsExport::class)
@file:Suppress("NON_EXPORTABLE_TYPE")

// No `package` declaration, deliberately. Kotlin/JS mirrors the Kotlin package structure
// into the generated module, so a file in `com.garmin.fit` would export as
// `com.garmin.fit.decodeFit(...)` in JavaScript and force
// `import { com } from '@glandais/fit-kotlin-sdk'` in TypeScript. The root package gives
// the flat, idiomatic surface a JS consumer expects:
//
// ```js
// import { decodeFit } from '@glandais/fit-kotlin-sdk'
// const { messages } = decodeFit(new Int8Array(await file.arrayBuffer()))
// ```

import com.garmin.fit.DecodeMode
import com.garmin.fit.DecodeOptions
import com.garmin.fit.Factory
import com.garmin.fit.FitDateTime
import com.garmin.fit.FitDecoder
import com.garmin.fit.FitEncoder
import com.garmin.fit.FitValues
import com.garmin.fit.Mesg
import com.garmin.fit.Profile
import com.garmin.fit.SubFieldSelector
import kotlin.js.ExperimentalJsExport
import kotlin.js.JsExport

/**
 * Knobs on [decodeFit]. Every field is optional; the defaults are the ones the Kotlin
 * [DecodeOptions] uses, plus [applyTypes].
 *
 * External, and so absent from the generated .d.ts, deliberately. Kotlin brands every
 * interface it exports with a `__doNotUseOrImplementIt` member, which is right for a type
 * the library hands *out* — nothing else can forge one — and fatal for a type the caller
 * has to build: `decodeFit(bytes, { applyTypes: false })` would not typecheck against it.
 * Hence [decodeFit] taking `Any?` and casting here. The shape is the KDoc below.
 */
public external interface FitDecodeOptions {
    /** `"normal"` (default), `"skipHeader"` or `"dataOnly"`. */
    public val mode: String?

    /** Unpack bit-packed fields into the fields they represent. Default true. */
    public val expandComponents: Boolean?

    /** Keep messages and fields the profile does not know. Default false. */
    public val includeUnknownData: Boolean?

    /** Fold `hr` messages into the `record` messages they overlap. Default true. */
    public val mergeHeartRates: Boolean?

    /**
     * Render profile types the way a JavaScript caller expects rather than as raw
     * numbers — `"cycling"` for a sport, a `Date` for a timestamp, `true`/`false` for a
     * boolean. Default true, matching `@garmin/fitsdk`. Turn it off to get exactly what
     * the file stores.
     */
    public val applyTypes: Boolean?
}

/** Something [decodeFit] could not make sense of. Decoding does not stop at the first one. */
@JsExport
public interface FitDecodeError {
    public val message: String

    /** Byte offset the problem was found at, or -1 when it is not tied to one. */
    public val bytePosition: Int
}

/** One decoded FIT message. */
@JsExport
public interface FitMessage {
    /** Profile name, lower camel case: `record`, `fileId`. `unknown` off-profile. */
    public val name: String

    /** Global message number. */
    public val num: Int

    /** Position of this message in the file, counting from 0. */
    public val index: Int

    /**
     * Field values by name, lower camel case: `{ positionLat: 539087456, heartRate: 140 }`.
     * A field the file left at FIT's invalid sentinel is absent rather than null. Fields
     * with several values hold an array. TypeScript: `Record<string, any>`.
     */
    public val fields: Any?

    /** Developer field values by name, same shape as [fields]. */
    public val developerFields: Any?
}

/** Everything [decodeFit] produced. */
@JsExport
public interface FitDecodeResult {
    /** Profile version from the first file header, or null when there was none. */
    public val profileVersion: Double?

    /** Empty when the whole input decoded cleanly. */
    public val errors: Array<FitDecodeError>

    /**
     * Messages grouped by type, under the key `@garmin/fitsdk` uses:
     * `{ recordMesgs: [...], sessionMesgs: [...] }`. The objects are the same ones
     * [mesgs] holds, not copies. TypeScript: `Record<string, FitMessage[]>`.
     */
    public val messages: Any?

    /** Every message, in the order it appears in the file. */
    public val mesgs: Array<FitMessage>
}

/** One message to write, as [encodeFit] takes it. External for the reason [FitDecodeOptions] is. */
public external interface FitMesgInput {
    /** Profile message name, lower camel case: `fileId`, `record`. */
    public val name: String

    /**
     * Field values by name, lower camel case. Numbers, strings, booleans, `Date`s and
     * profile value names (`"cycling"`) are all accepted; arrays set a repeated field.
     * TypeScript: `Record<string, any>`.
     */
    public val fields: Any?
}

/** What the profile says about one field, as [fitFieldInfo] reports it. */
@JsExport
public interface FitFieldInfo {
    public val name: String
    public val num: Int
    public val units: String
    public val scale: Double
    public val offset: Double

    /** FIT base type: `uint8`, `sint32`, `string`… */
    public val baseType: String

    /** Profile type when the field has named values (`sport`), otherwise null. */
    public val type: String?
}

/** True when [bytes] opens with a plausible FIT header. */
@JsExport
public fun isFitFile(bytes: ByteArray): Boolean = FitDecoder.isFit(bytes)

/**
 * True when every chained file's header CRC and file CRC check out.
 *
 * A whole pass over the file, so [decodeFit] on the same bytes costs about as much again;
 * `decodeFit(...).errors` already reports a bad CRC.
 */
@JsExport
public fun checkFitIntegrity(bytes: ByteArray): Boolean = FitDecoder(bytes).checkIntegrity()

/**
 * Decodes a FIT file.
 *
 * Never throws on malformed input: whatever decoded before the problem comes back
 * alongside a description of it in [FitDecodeResult.errors].
 *
 * ```js
 * const result = decodeFit(new Int8Array(await file.arrayBuffer()))
 * for (const record of result.messages.recordMesgs ?? []) console.log(record.fields.heartRate)
 * ```
 *
 * A `uint64` field beyond 2^53 arrives rounded: JavaScript numbers carry 53 bits of
 * mantissa. Only the FIT `*_64` types and a handful of device serial fields can reach
 * that far.
 */
@JsExport
public fun decodeFit(bytes: ByteArray, options: Any? = null): FitDecodeResult {
    val opts = options?.unsafeCast<FitDecodeOptions>()
    val decodeOptions = DecodeOptions(
        mode = when (opts?.mode) {
            "skipHeader" -> DecodeMode.SKIP_HEADER
            "dataOnly" -> DecodeMode.DATA_ONLY
            else -> DecodeMode.NORMAL
        },
        expandComponents = opts?.expandComponents ?: true,
        includeUnknownData = opts?.includeUnknownData ?: false,
        mergeHeartRates = opts?.mergeHeartRates ?: true,
    )
    val applyTypes = opts?.applyTypes ?: true

    val decoded = FitDecoder(bytes).decode(decodeOptions)

    val grouped = js("({})")
    val ordered = ArrayList<FitMessage>(decoded.mesgs.size)
    for (mesg in decoded.mesgs) {
        val converted = convertMesg(mesg, applyTypes)
        ordered.add(converted)
        val key = jsName(mesg.mesgName) + "Mesgs"
        var bucket = grouped[key]
        if (bucket == null) {
            bucket = js("[]")
            grouped[key] = bucket
        }
        bucket.push(converted)
    }

    val result = js("({})")
    result.profileVersion = decoded.profileVersion?.toDouble()
    result.errors = decoded.errors.map { error ->
        val e = js("({})")
        e.message = error.message
        e.bytePosition = error.bytePosition
        e.unsafeCast<FitDecodeError>()
    }.toTypedArray()
    result.messages = grouped
    result.mesgs = ordered.toTypedArray()
    return result.unsafeCast<FitDecodeResult>()
}

/**
 * Writes a FIT file.
 *
 * The first message must be a `fileId`; nothing here enforces it, but readers reject a
 * file without one.
 *
 * ```js
 * const bytes = encodeFit([
 *     { name: 'fileId', fields: { type: 'activity', manufacturer: 'garmin', timeCreated: new Date() } },
 *     { name: 'record', fields: { timestamp: new Date(), heartRate: 140 } },
 * ])
 * ```
 *
 * Throws when a message name is not in the profile — a typo there would otherwise write a
 * file that decodes to nothing.
 */
@JsExport
public fun encodeFit(messages: Array<Any>): ByteArray {
    val encoder = FitEncoder()
    for (input in messages) encoder.write(buildMesg(input.unsafeCast<FitMesgInput>()))
    return encoder.close()
}

/** The FIT profile version this SDK was generated from, as `"21.205.0"`. */
@JsExport
public fun fitProfileVersion(): String =
    "${Profile.VERSION_MAJOR}.${Profile.VERSION_MINOR}.${Profile.VERSION_BUILD}"

/** Every message name the profile knows, lower camel case, in profile order. */
@JsExport
public fun fitMessageNames(): Array<String> = ProfileNames.mesgNames().map { jsName(it) }.toTypedArray()

/**
 * What the profile says about one field, or null when either name is not in the profile.
 *
 * Both names are the lower camel case ones [decodeFit] hands back, so a caller holding a
 * decoded message can ask what its units are without a lookup table of its own.
 */
@JsExport
public fun fitFieldInfo(mesgName: String, fieldName: String): FitFieldInfo? {
    val mesgNum = ProfileNames.mesgNumOf(profileName(mesgName)) ?: return null
    val field = Factory.createMesg(mesgNum.toUShort()).getField(profileName(fieldName)) ?: return null
    val info = js("({})")
    info.name = fieldName
    info.num = field.fieldNum.toInt()
    info.units = field.units
    info.scale = field.scale
    info.offset = field.offset
    info.baseType = field.baseType.name.lowercase()
    info.type = ProfileNames.typeOf(mesgNum, field.fieldName)
    return info.unsafeCast<FitFieldInfo>()
}

// ------------------------------------------------------------------------ decode helpers

private fun convertMesg(mesg: Mesg, applyTypes: Boolean): FitMessage {
    val fields = js("({})")
    for (field in mesg.fieldList) {
        if (!field.hasValues) continue
        // The active subfield decides both the name and the meaning: `event.data` is
        // `gearChangeData` on a rear_gear_change event, and reading it as anything else
        // would give a number under the wrong interpretation.
        val profileFieldName = field.nameOf(SubFieldSelector.Active, mesg)
        val typeName = ProfileNames.typeOf(mesg.globalMesgNum.toInt(), profileFieldName)
        val values = List(field.numValues) {
            mesg.getFieldValue(field.fieldNum, it, SubFieldSelector.Active)
        }
        val key = if (field.fieldName == UNKNOWN_FIELD) "field${field.fieldNum}" else jsName(profileFieldName)
        fields[key] = jsValues(values, typeName, applyTypes)
    }

    val developerFields = js("({})")
    for (field in mesg.developerFieldList) {
        if (!field.hasValues) continue
        val values = List(field.numValues) { field.getValue(it) }
        developerFields[jsName(field.fieldName)] = jsValues(values, null, applyTypes)
    }

    val out = js("({})")
    out.name = jsName(mesg.mesgName)
    out.num = mesg.globalMesgNum.toInt()
    out.index = mesg.decoderMesgIndex
    out.fields = fields
    out.developerFields = developerFields
    return out.unsafeCast<FitMessage>()
}

/** One value on its own, several as an array — the shape `@garmin/fitsdk` produces. */
private fun jsValues(values: List<Any?>, typeName: String?, applyTypes: Boolean): Any? =
    if (values.size == 1) {
        jsValue(values[0], typeName, applyTypes)
    } else {
        values.map { jsValue(it, typeName, applyTypes) }.toTypedArray()
    }

private fun jsValue(value: Any?, typeName: String?, applyTypes: Boolean): Any? {
    if (value == null) return null
    if (value is String) return value

    // Deliberately not `value is Double` and friends: on the js target Byte, Short, Int,
    // Float and Double are all one JS number, so those checks do not distinguish. The
    // boxed unsigned types are real objects there, which is why this goes through
    // FitValues rather than a cast.
    val number = FitValues.toDouble(value)
    if (number.isNaN()) return value.toString()

    if (!applyTypes || typeName == null) return number

    when (typeName) {
        // A date_time is a count of seconds, not a named value, and its profile entries
        // (`min`) name a threshold rather than an instant.
        "date_time", "local_date_time" -> {
            val instant = FitDateTime.toInstant(number.toLong().toUInt()) ?: return number
            return jsDate(instant.toEpochMilliseconds().toDouble())
        }
        "bool" -> return number != 0.0
    }

    return ProfileNames.nameOf(typeName, number) ?: number
}

// ------------------------------------------------------------------------ encode helpers

private fun buildMesg(input: FitMesgInput): Mesg {
    val profileMesgName = profileName(input.name)
    val mesgNum = ProfileNames.mesgNumOf(profileMesgName)
        ?: throw IllegalArgumentException("no FIT message named '${input.name}'")

    val mesg = Factory.createMesg(mesgNum.toUShort())
    val fields = input.fields
    if (fields != null) {
        for (key in objectKeys(fields)) {
            val field = mesg.getField(profileName(key))
                ?: throw IllegalArgumentException("'${input.name}' has no field named '$key'")
            val typeName = ProfileNames.typeOf(mesgNum, field.fieldName)
            val value = fields.asDynamic()[key]
            if (isJsArray(value)) {
                val array = value.unsafeCast<Array<Any?>>()
                for (index in array.indices) {
                    mesg.setFieldValue(field.fieldNum, kotlinValue(array[index], typeName), index)
                }
            } else {
                mesg.setFieldValue(field.fieldNum, kotlinValue(value, typeName))
            }
        }
    }

    // Factory.createMesg carries every field the profile declares; the copy constructor
    // drops the ones left without a value, so only what the caller set is written.
    return Mesg(mesg)
}

private fun kotlinValue(value: Any?, typeName: String?): Any? {
    if (value == null) return null
    if (isJsDate(value)) {
        return FitDateTime.toTimestamp(kotlin.time.Instant.fromEpochMilliseconds(jsDateMillis(value).toLong()))
    }
    if (value is String) {
        // A profile value name where the field has one; anything else is a real string.
        if (typeName != null) ProfileNames.valueOf(typeName, value)?.let { return it }
        return value
    }
    if (value is Boolean) return if (value) 1.0 else 0.0
    return value
}

// ------------------------------------------------------------------------ small helpers

/** `PositionLat` to `positionLat`: profile spelling to the one JavaScript expects. */
private fun jsName(name: String): String =
    if (name.isEmpty()) name else name[0].lowercaseChar() + name.substring(1)

/** The inverse of [jsName]. */
private fun profileName(name: String): String =
    if (name.isEmpty()) name else name[0].uppercaseChar() + name.substring(1)

/** What [Factory] names a message or field the profile does not know. */
private const val UNKNOWN_FIELD: String = "unknown"

private fun jsDate(millis: Double): Any = js("new Date(millis)")

private fun isJsDate(value: Any?): Boolean = js("value instanceof Date").unsafeCast<Boolean>()

private fun jsDateMillis(value: Any?): Double = value.asDynamic().getTime().unsafeCast<Double>()

private fun isJsArray(value: Any?): Boolean = js("Array.isArray(value)").unsafeCast<Boolean>()

private fun objectKeys(value: Any?): Array<String> = js("Object.keys(value)").unsafeCast<Array<String>>()
